import { useCallback, useEffect, useMemo, useState } from 'react'

import { stepPage } from './ebook-pages.ts'

/**
 * Dwell time per page when the book is playing itself.
 *
 * TWELVE SECONDS, DERIVED FROM THE COLLECTION. The longest poem runs 34 lines and
 * the mean is under 20; at an unhurried 2.5 seconds a line nobody finishes the
 * longest one in twelve. That is deliberate — the auto-advance is a way to watch
 * the book turn its own pages, not a substitute for reading it, and a reader who
 * wants to finish a page has a Pause button and two arrow keys that both work
 * while it runs.
 */
export const AUTO_ADVANCE_MS = 12_000

const NEXT_KEY = 'ArrowRight'
const PREVIOUS_KEY = 'ArrowLeft'

export interface EbookState {
  readonly isOpen: boolean
  readonly index: number
  readonly isPlaying: boolean
  readonly open: () => void
  readonly close: () => void
  readonly next: () => void
  readonly previous: () => void
  readonly togglePlay: () => void
}

/**
 * The fullscreen reader's state: which page, playing or not, open or not.
 *
 * IT OPENS PAUSED, ALWAYS. rules/accessibility.md § 5 bans auto-playing media
 * outright, and a book that started turning its own pages the moment it opened
 * would be exactly that — motion the reader did not ask for, begun before they
 * have found the control that stops it. Play is a thing you press.
 *
 * NOTHING IS PERSISTED. No storage, no cookie, no server state: the site is
 * stateless end to end, and a reader returning to page seventeen of a book they
 * did not know they had left open is not a feature anyone asked for.
 *
 * `open()` RESETS. Reopening from a poem page puts you on that poem, not wherever
 * you happened to stop last time, because the launcher that was just pressed is
 * the one that says where the book starts.
 */
export function useEbook(total: number, initialIndex: number): EbookState {
  const [isOpen, setOpen] = useState(false)
  const [index, setIndex] = useState(initialIndex)
  const [isPlaying, setPlaying] = useState(false)

  const open = useCallback(() => {
    setIndex(initialIndex)
    setPlaying(false)
    setOpen(true)
  }, [initialIndex])

  const close = useCallback(() => {
    setPlaying(false)
    setOpen(false)
  }, [])

  const next = useCallback(() => {
    setIndex((current) => stepPage(current, total, 1))
  }, [total])

  const previous = useCallback(() => {
    setIndex((current) => stepPage(current, total, -1))
  }, [total])

  const togglePlay = useCallback(() => {
    setPlaying((playing) => !playing)
  }, [])

  /**
   * Synchronises with the platform timer.
   *
   * A legitimate effect under rules/frontend.md § 2: `setInterval` is an external
   * system with no React equivalent, and the interval exists only while the book
   * is actually playing — pausing tears it down rather than letting it fire into
   * a guard. `isPlaying` can only be true while the reader is open, because both
   * `open` and `close` set it false.
   */
  useEffect(() => {
    if (!isPlaying) return

    const timer = setInterval(() => {
      setIndex((current) => stepPage(current, total, 1))
    }, AUTO_ADVANCE_MS)

    return () => {
      clearInterval(timer)
    }
  }, [isPlaying, total])

  /**
   * Synchronises with the document's key events.
   *
   * ON `document`, NOT ON THE DIALOG, and not because it is easier. A React
   * `onKeyDown` would have to hang off the `<dialog>` element, which carries the
   * non-interactive `dialog` role — `jsx-a11y`'s strict preset rejects keyboard
   * handlers there, and it is right to: the arrow keys here are a property of the
   * reader being open, not of any one element being focused. While it is open the
   * dialog is modal, so nothing behind it can see these keys either way.
   *
   * `Escape` is absent on purpose. A modal `<dialog>` closes on `Escape` natively
   * and fires `close` doing it, which `use-fullscreen-dialog.ts` is already
   * listening for. Handling it here too would close the book twice.
   */
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === NEXT_KEY) setIndex((current) => stepPage(current, total, 1))
      else if (event.key === PREVIOUS_KEY) setIndex((current) => stepPage(current, total, -1))
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, total])

  return useMemo(
    () => ({ isOpen, index, isPlaying, open, close, next, previous, togglePlay }),
    [isOpen, index, isPlaying, open, close, next, previous, togglePlay],
  )
}
